import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, store } from "../../../store/store";
import { setStartTime, setEndTime } from "../../../store/orm/userSession";
import ormStationsService from "../../../hooks/api/stations";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useKeydownListener } from "../../../hooks/useKeydownListener";

export interface TimeConfigData {
  min_start_date: string;
  selected_start_date: string;
  max_start_date: string;
  min_end_date: string;
  selected_end_date: string;
  max_end_date: string;
  date_Format: string;
  using_offer_times: string;
  start_time_config: string[];
  end_time_config: string[];
  closed_days_config: any[];
  start_closed_days_config: any[];
  end_closed_days_config: any[];
}

export type PickerStep = "hour" | "minute";

interface TimePickerProps {
  onTimeChange: (newTime: string) => void;
  type: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ onTimeChange, type }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hourDisplayRef = useRef<HTMLDivElement>(null);
  const minuteDisplayRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const openingtimes = useSelector(
    (state: RootState) => state.stations.openingtimes,
  );

  const dispatch = useDispatch();

  const [timeConfig, setTimeConfig] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState<PickerStep>("hour");
  const [selectedHour, setSelectedHour] = useState<string>("");
  const [selectedMinute, setSelectedMinute] = useState<string>("");
  const [firstclickbuffer, setFirstclickbuffer] = useState<boolean>(false);
  const [focusedGridIndex, setFocusedGridIndex] = useState<number>(0);

  useEffect(() => {
    containerRef.current?.focus();
    // Focus on hour display when component mounts
    // setTimeout(() => hourDisplayRef.current?.focus(), 0);

    if (Object.keys(openingtimes).length > 0) {
      let selectedDate = "";
      if (type === "start") {
        selectedDate = openingtimes.selected_start_date;
        setTimeConfig(openingtimes.start_time_config);
      } else {
        selectedDate = openingtimes.selected_end_date;
        setTimeConfig(openingtimes.end_time_config);
      }

      const initialTime = selectedDate.split("T")[1].substring(0, 5);
      setSelectedHour(initialTime.split(":")[0]);
      setSelectedMinute(initialTime.split(":")[1]);
    }
  }, [openingtimes, type]);

  const availableHours = useMemo<string[]>(() => {
    const hoursSet = new Set(timeConfig.map((t) => t.split(":")[0]));
    const hoursArray = Array.from(hoursSet);
    return hoursArray.sort((a, b) => parseInt(a) - parseInt(b));
  }, [timeConfig]);

  const availableMinutes = useMemo<string[]>(() => {
    return timeConfig
      .filter((t) => t.startsWith(selectedHour))
      .map((t) => t.split(":")[1])
      .sort((a, b) => parseInt(a) - parseInt(b));
  }, [selectedHour, timeConfig]);

  const handleHourClick = (h: string): void => {
    setSelectedHour(h);
    const availableMinutesForNewHour = timeConfig
      .filter((t) => t.startsWith(h))
      .map((t) => t.split(":")[1]);

    if (!availableMinutesForNewHour.includes(selectedMinute)) {
      const firstMinute = availableMinutesForNewHour.sort(
        (a, b) => parseInt(a) - parseInt(b),
      )[0];
      setSelectedMinute(firstMinute || "00");
    }
    setActiveStep("minute");
    setTimeout(() => {
      const newIndex = 0;
      setFocusedGridIndex(newIndex);
      const buttons = gridRef.current!.querySelectorAll(".orm-time-picker-btn");
      (buttons[newIndex] as HTMLButtonElement)?.focus();
    }, 10);
  };

  const handleMinuteClick = (m: string): void => {
    setSelectedMinute(m);
  };

  const changeTime = () => {
    if (type === "start") {
      dispatch(setStartTime(`${selectedHour}:${selectedMinute}`));
    } else {
      dispatch(setEndTime(`${selectedHour}:${selectedMinute}`));
    }
    onTimeChange(`${selectedHour}:${selectedMinute}`);
    onTimeChange("done");
    ormStationsService.fetchOpeningTimes(store.getState().userSession);
  };

  const handleClickoutside = (event: any) => {
    if (event.target.classList.contains("orm-time-picker-btn")) return;
    if (!firstclickbuffer) {
      setFirstclickbuffer(true);
    } else {
      onTimeChange("done");
    }
  };

  useClickOutside(containerRef, handleClickoutside);

  // Keyboard navigation
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const currentList =
        activeStep === "hour" ? availableHours : availableMinutes;

      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          event.preventDefault();
          // Navigate through grid buttons
          if (gridRef.current?.contains(activeElement as Node)) {
            const newIndex = (focusedGridIndex + 1) % currentList.length;
            setFocusedGridIndex(newIndex);
            const buttons = gridRef.current.querySelectorAll(
              ".orm-time-picker-btn",
            );
            (buttons[newIndex] as HTMLButtonElement)?.focus();
          } else {
            const newIndex = 0;
            setFocusedGridIndex(newIndex);
            const buttons = gridRef.current!.querySelectorAll(
              ".orm-time-picker-btn",
            );
            (buttons[newIndex] as HTMLButtonElement)?.focus();
          }
          break;

        case "ArrowUp":
        case "ArrowLeft":
          event.preventDefault();

          // Navigate through grid buttons
          if (gridRef.current?.contains(activeElement as Node)) {
            const newIndex =
              (focusedGridIndex - 1 + currentList.length) % currentList.length;
            setFocusedGridIndex(newIndex);
            const buttons = gridRef.current.querySelectorAll(
              ".orm-time-picker-btn",
            );
            (buttons[newIndex] as HTMLButtonElement)?.focus();
          } else {
            const newIndex = 0;
            setFocusedGridIndex(newIndex);
            const buttons = gridRef.current!.querySelectorAll(
              ".orm-time-picker-btn",
            );
            (buttons[newIndex] as HTMLButtonElement)?.focus();
          }
          break;

        case "Enter":

        case " ":
          event.preventDefault();

          // Execute action based on focused element
          if (activeElement === hourDisplayRef.current) {
            setActiveStep("hour");
          } else if (activeElement === minuteDisplayRef.current) {
            setActiveStep("minute");
          } else if (activeElement === submitButtonRef.current) {
            changeTime();
          } else if (gridRef.current?.contains(activeElement as Node)) {
            (activeElement as HTMLButtonElement)?.click();
          }
          break;

        case "Escape":
          event.preventDefault();
          onTimeChange("done");
          break;

        case "Tab":
          event.preventDefault();

          // Tab loop: hourDisplay -> minuteDisplay -> submitButton -> back to hourDisplay
          if (activeElement === hourDisplayRef.current) {
            minuteDisplayRef.current?.focus();
          } else if (activeElement === minuteDisplayRef.current) {
            submitButtonRef.current?.focus();
          } else if (activeElement === submitButtonRef.current) {
            hourDisplayRef.current?.focus();
          } else {
            // If focus is somewhere else, go to hour display
            hourDisplayRef.current?.focus();
          }
          break;

        default:
          break;
      }
    },
    [
      activeStep,
      availableHours,
      availableMinutes,
      focusedGridIndex,
      selectedHour,
      selectedMinute,
    ],
  );

  useKeydownListener(containerRef, handleKeydown);

  // Reset grid focus index when active step changes
  useEffect(() => {
    setFocusedGridIndex(0);
  }, [activeStep]);

  return (
    <div
      className="orm-time-picker-container"
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-label={`Zeitauswahl für ${type === "start" ? "Abholung" : "Abgabe"}`}
      aria-modal="true"
    >
      <div
        className="orm-time-picker-display-header"
        role="group"
        aria-label="Zeitauswahl"
      >
        <div
          ref={hourDisplayRef}
          onClick={() => setActiveStep("hour")}
          className={`orm-time-picker-display-unit ${activeStep === "hour" ? "active" : ""}`}
          tabIndex={0}
          role="button"
          aria-label={`Stunde ${selectedHour}`}
          aria-pressed={activeStep === "hour"}
        >
          {selectedHour}
        </div>
        <span className="orm-time-picker-separator" aria-hidden="true">
          :
        </span>
        <div
          ref={minuteDisplayRef}
          onClick={() => setActiveStep("minute")}
          className={`orm-time-picker-display-unit ${activeStep === "minute" ? "active" : ""}`}
          tabIndex={0}
          role="button"
          aria-label={`Minute ${selectedMinute}`}
          aria-pressed={activeStep === "minute"}
        >
          {selectedMinute}
        </div>
      </div>
      <div className="orm-time-picker-subheader">
        {activeStep === "hour" ? "Stunde wählen" : "Minute wählen"}
      </div>
      <div
        className="orm-time-picker-selection-grid"
        ref={gridRef}
        role="listbox"
        aria-label={
          activeStep === "hour" ? "Verfügbare Stunden" : "Verfügbare Minuten"
        }
      >
        {activeStep === "hour"
          ? availableHours.map((hour, index) => (
              <button
                key={hour}
                onClick={() => handleHourClick(hour)}
                className={`orm-time-picker-btn ${selectedHour === hour ? "selected" : ""}`}
                tabIndex={-1}
                role="option"
                aria-selected={selectedHour === hour}
                aria-label={`${hour} Uhr`}
              >
                {hour}
              </button>
            ))
          : availableMinutes.map((min, index) => (
              <button
                key={min}
                onClick={() => handleMinuteClick(min)}
                className={`orm-time-picker-btn ${selectedMinute === min ? "selected" : ""}`}
                tabIndex={-1}
                role="option"
                aria-selected={selectedMinute === min}
                aria-label={`${min} Minuten`}
              >
                {min}
              </button>
            ))}
      </div>

      <div className="orm-time-picker-footer">
        <button
          ref={submitButtonRef}
          className="orm-button__cta small"
          onClick={changeTime}
          tabIndex={0}
          aria-label="Zeit bestätigen und auswählen"
        >
          Auswählen
        </button>
      </div>
    </div>
  );
};

export default TimePicker;
